import React from "react";
import useLocale from "../util/i18n";
import request from "../util/erp/unleashed";
import Select, { type OptionType } from "../component/select";
import { MDBBtn, MDBCard, MDBCardBody, MDBCol, MDBInput, MDBInputGroup, MDBRow, MDBTable, MDBTableBody, MDBTableHead, MDBTooltip } from "mdb-react-ui-kit";
import { useCrumbs } from "../component/breadcrumbs";
import { Helmet } from "react-helmet-async";
import { getProductGroups, getProducts, getPurchaseOrderItem, getSoldQty, getStockQty, syncProducts, syncPurchaseOrders, syncSalesOrders, syncStockQty, syncCreditMemo, getRefundQty, type Product, type PurchaseOrderItem, type SkuQty } from "../api/erp";
import BigNumber from "bignumber.js";
import { type ListResponse } from "../api/types";
import Toast from "../component/toast";
import ExcelJS from "exceljs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAnglesDown, faAnglesUp, faInfoCircle, faRotate, faSpinner } from "@fortawesome/free-solid-svg-icons";
import timezones from "../source/timezone.json";
import { fromZonedTime } from "date-fns-tz";
import { startOfMonth, getUnixTime } from "date-fns";

const months = [
    { value: '1', label: '1' }, { value: '2', label: '2' }, { value: '3', label: '3' }, { value: '4', label: '4' },
    { value: '5', label: '5' }, { value: '6', label: '6' }, { value: '7', label: '7' }, { value: '8', label: '8' },
    { value: '9', label: '9' }, { value: '10', label: '10' }, { value: '11', label: '11' }, { value: '12', label: '12' }
];

interface TableProps {
    supplier?: string;
    product_group: string;
    scpt: string;
    coft: string;
    mqf: string;
    timezone: string;
    stat_sale: string;
    fsi_1: string;
    fsi_2?: string;
    fsi_3?: string;
    fsi_4?: string;
    fsi_5?: string;
    fsi_6?: string;
    fsi_7?: string;
    fsi_8?: string;
    fsi_9?: string;
    fsi_10?: string;
    fsi_11?: string;
    fsi_12?: string;
    unit: 'm' | 'ft' | 'in'
}

export default function Forecast() {
    let { 0: groups, 1: setGroups } = React.useState<Array<OptionType>>([]);
    let { 0: params, 1: setParams } = React.useState<TableProps | null>(null);
    let { 0: timezone, 1: setTimezone } = React.useState<string>(localStorage.getItem('timezone') ?? '');
    let { 0: fsi, 1: setFsi } = React.useState('3');
    let { 0: __ } = useLocale();
    const loadSupplier = React.useCallback(async () => {
        let options: Array<OptionType> = [];
        let response = await request('Suppliers');
        for (let i of (response.Items || [])) {
            options.push({
                value: i.Guid, label: i.SupplierName
            });
        }
        return options;
    }, []);
    const loadProductGroup = React.useCallback(async () => {
        let options: Array<OptionType> = [];
        let response = await getProductGroups();
        if (typeof response !== 'string') {
            for (let item of response) {
                options.push({
                    value: item, label: item
                });
            }
        }
        setGroups(options);
    }, [setGroups]);
    const {0: setCrumbs} = useCrumbs();
    const onSubmit = React.useCallback((e: React.FormEvent<HTMLFormElement>) => {
        e.stopPropagation();
        e.preventDefault();
        let data = new FormData(e.target as HTMLFormElement);
        let itt = (new BigNumber(data.get('scpt') as string)).plus(data.get('coft') as string);
        let date = fromZonedTime(Date.now(), timezone);
        if (itt.gt(diff(date.getMonth() + parseInt(fsi), date.getMonth() + 1, date.getFullYear()))) {
            Toast.show(__('The In Transit Time should be less than the time of Forecast Sales Increase.'));
            return;
        }
        let result: any = {};
        for (const [k, v] of data.entries()) {
            result[k as keyof TableProps] = v as string;
        }
        setParams(result);
    }, [setParams, fsi, __, timezone]);
    const doExport = React.useRef<() => void>(null);
    const onExport = React.useCallback((e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e.stopPropagation();
        e.preventDefault();
        doExport.current?.();
    }, []);
    React.useEffect(() => {
        localStorage.setItem('timezone', timezone);
    }, [timezone]);
    React.useEffect(() => {
        loadProductGroup()
    }, [loadProductGroup]);
    React.useEffect(() => {
        setCrumbs(['Forecast']);
    }, []);
    return (
        <div className="forecast">
            <Helmet>
                <title>{__('Forecast')}</title>
            </Helmet>
            <MDBCard className="hidden-print">
                <MDBCardBody tag="form" onSubmit={onSubmit}>
                    <MDBRow className="g-3">
                        <MDBCol size="12" lg="6" xxl="4" className="d-flex flex-column gap-3">
                            <h3>{__('Products')}</h3>
                            {/* <Select load={loadSupplier} name="supplier" label={__('Supplier')} /> */}
                            <Select options={groups} name="product_group" label={__('Product Group')} />
                            <Select name="unit" label={__('Unit')} defaultValue="in" options={[
                                { label: __('Inches'), value: 'in' },
                                { label: __('Feet'), value: 'ft' },
                                { label: __('Meters'), value: 'm' },
                            ]} />
                            <Select name="timezone" label={__('Timezone')} options={timezones.map(value => ({ value, label: value }))} value={timezone} onChange={setTimezone} />
                        </MDBCol>
                        <MDBCol size="12" lg="6" xxl="4" className="d-flex flex-column gap-3">
                            <h3>{__('In Transit Time')}</h3>
                            <MDBInputGroup textAfter={__('Days')}>
                                <MDBInput type="number" name="scpt" label={__('Supplier Current Production Time')} defaultValue={10} />
                            </MDBInputGroup>
                            <MDBInputGroup textAfter={__('Days')}>
                                <MDBInput type="number" name="coft" label={__('Current Ocean Freight Time')} defaultValue={10} />
                            </MDBInputGroup>
                        </MDBCol>
                        <MDBCol size="12" lg="6" xxl="4" className="d-flex flex-column gap-3">
                            <h3>{__('Statistical Period')}</h3>
                            <MDBInputGroup textAfter={__('Months')}>
                                <Select name="stat_sale" label={__('Sales')} options={months} defaultValue="12" />
                            </MDBInputGroup>
                            <MDBInputGroup textAfter={__('Months')}>
                                <Select label={__('Forecasting Period')} options={months} value={fsi} onChange={setFsi} />
                            </MDBInputGroup>
                            <MDBInputGroup textAfter={__('Days')}>
                                <MDBInput type="number" name="mqf" label={__('Minimal Quantity Factor')} defaultValue={20} />
                            </MDBInputGroup>
                        </MDBCol>
                        <MDBCol size="12">
                            <h3 className="mb-4">{__('Forecast Sales Increase')}</h3>
                            <MDBRow className="g-3">
                                {Object.keys((new Array(parseInt(fsi))).fill(true)).map(month => (
                                    <MDBCol key={'fsi_' + month} size="12" sm="6" lg="4" xxl="3">
                                        <MDBInputGroup textAfter="%">
                                            <MDBInput name={'fsi_' + (parseInt(month) + 1)} type="number" label={__('Next %d Month Increment').replace('%d', (parseInt(month) + 1).toString())} defaultValue={10} />
                                        </MDBInputGroup>
                                    </MDBCol>
                                ))}
                            </MDBRow>
                        </MDBCol>
                        <MDBCol size="12" className="text-center">
                            <MDBBtn type="submit" className="px-4">{__('Calc')}</MDBBtn>
                            {params ? (<MDBBtn type="button" color="secondary" className="px-4 ms-3" onClick={onExport}>{__('Export')}</MDBBtn>) : null}
                        </MDBCol>
                    </MDBRow>
                </MDBCardBody>
            </MDBCard>
            {params ? (<Table {...params} timezone={timezone} ref={doExport} />) : null}
        </div>
    );
}

interface Row {
    sku: string;
    size: number;
    w: number;
    h: number;
    d: number;
    price: number;
    aq?: number;
    aus?: BigNumber;
    ausd?: number;
    ausq?: number;
    ref?: number;
    mq?: BigNumber;
    mqd?: number;
    mqq?: number;
    rmq?: number;
    op?: BigNumber;
    delivered_at?: number | null;
    ordered_at?: number;
}

function diff(month: number, last: number, year: number, days = 0): number {
    while (last <= 0) {
        last += 12;
    }
    switch (month) {
        case 4: case 6: case 9: case 11: days += 30; break;
        case 2: days += year % 100 !== 0 && year % 4 === 0 || year % 400 === 0 ? 29 : 28; break;
        default: days += 31;
    }
    return month === last ? days : days + diff(month - 1 <= 0 ? 11 + month : month - 1, last, days);
}

const Table = React.forwardRef<() => void, TableProps>(function (props, ref) {
    let { 0: items, 1: setItems } = React.useState<Array<Row>>([]);
    let { 0: filter, 1: setFilter } = React.useState(false);
    let { 0: __, 1: lang } = useLocale();
    let formatter = React.useMemo(() => new Intl.NumberFormat(lang, { style: 'currency', currency: 'USD' }), [lang]);
    let opt = React.useCallback((item: Row) => {
        let result = new BigNumber(props.coft).times(86400);
        if (item.delivered_at) {
            result = new BigNumber(item.delivered_at);
        } else if (item.ordered_at) {
            result = result.plus(item.ordered_at || 0).plus(new BigNumber(props.scpt).times(86400));
        } else {
            return '-';
        }
        result = result.minus(Date.now() / 1000).div(86400);
        return parseFloat(result.toFixed(0, BigNumber.ROUND_CEIL));
    }, [props]);
    let itt = React.useMemo(() => (new BigNumber(props.coft || 10)).plus(props.scpt || 10).toNumber(), [props]);
    let head = React.useMemo(() => {
        return [
            { key: 'SKU', title: 'SKU', className: 'text-start' },
            { key: 'AQ', title: __('Available Quantity') },
            { key: 'OP', title: __('On Purchase Quantity') },
            { key: 'OPT(' + __('Days') + ')', title: __('On Purchase Arrival Time') + '(' + __('Days') + ')' },
            { key: 'AUS', title: __('Average Unit Sales') },
            { key: 'ITT(' + __('Days') + ')', title: __('In Transit Time') + '(' + __('Days') + ')' },
            { key: 'FSI', title: __('Forecast Sales Increase') },
            { key: 'FUS', title: __('Forecast Unit Sales') },
            { key: 'MQ', title: __('Minimal Quantity') },
            { key: 'EPOQ', title: __('Estimate Purchase Order Quantity') },
            { key: 'FOB/Unit Price', title: __('Unit Price') },
            { key: 'Line TT', title: __('Line Total') },
            { key: 'CBM', title: __('CBM') },
            { key: 'CBM TT', title: __('CBM Total') }
        ];
    }, [__]);
    let body = React.useMemo(() => {
        let result: Array<Array<{ value: string | number, title?: string, className?: string }>> = [];
        let date = fromZonedTime(Date.now(), props.timezone);
        let year = date.getFullYear(), month = date.getMonth() + 1, days = diff(month, month, year);
        let t = new BigNumber(itt), m = 0, d = new BigNumber(0);
        for (let j = 0; ; j++) {
            let days = diff(month + j, month + j, year);
            if (t.gte(days)) {
                t = t.minus(days);
                m++;
            } else {
                d = t.div(days);
                break;
            }
        }
        for (let item of items) {
            let row: Array<{ value: string | number, title?: string, className?: string }> = [
                { value: item.sku, className: 'text-start' },
                { value: item.aq || 0 },
                { value: item.op?.toNumber() || 0 },
                { value: opt(item) },
                { value: parseFloat(item.aus?.toFixed(4) || '0'), title: '(' + item.ausq + (item.ref ? ('-' + item.ref) : '') + ')/' + item.ausd },
                { value: itt }
            ];
            let fsis: Array<BigNumber> = [], fsi = new BigNumber(0), fsit = '';
            for (let i = 1; i <= 12; i++) {
                if (!props['fsi_' + i as keyof TableProps]) {
                    break;
                }
                fsis[i - 1] = (i === 1 ? (item.aus || new BigNumber(0)).times(days) : fsis[i - 2]).times((new BigNumber(props['fsi_' + i as keyof TableProps] as string)).div(100).plus(1));
                fsi = fsi.plus(fsis[i - 1]);
                fsit += '+' + fsis[i - 1].toNumber();
            }
            let fus = new BigNumber(0), fust = '';
            for (let j = 1; j <= m; j++) {
                fus = fus.plus(fsis[j - 1]?.toNumber() || 0);
                fust += fsis[j - 1] ? ('+' + fsis[j - 1]?.toNumber()) : '';
            }
            fus = fus.plus(d.times(fsis[m]?.toNumber() || 0));
            fust += '+' + (fsis[m]?.toNumber() || 0) + '*' + d;
            let mq = item.mq?.times(props.mqf);
            let epoq = fus?.plus(mq || 0).minus(item.aq || 0).minus(item.op || 0).integerValue(BigNumber.ROUND_CEIL);
            result.push(row.concat([
                { value: parseInt(fsi?.toFixed(0, BigNumber.ROUND_CEIL)) || 0, title: fsit.substring(1) },
                { value: parseInt(fus?.toFixed(0, BigNumber.ROUND_CEIL)) || 0, title: fust.substring(1) },
                { value: parseInt(mq?.toFixed(0, BigNumber.ROUND_CEIL) || '0') || 0, title: '(' + item.mqq + (item.rmq ? ('-' + item.rmq) : '') + ')/' + item.mqd + '*' + props.mqf },
                { value: Math.max(0, epoq?.toNumber() || 0) || 0, title: (fus?.toFixed(0, BigNumber.ROUND_CEIL) || 0) + '+' + (mq?.toFixed(0, BigNumber.ROUND_CEIL) || 0) + '-' + (item.aq || 0) + '-' + (item.op || 0) },
                { value: formatter.format(item.price || 0) },
                { value: formatter.format(Math.max(0, epoq?.times(item.price).toNumber() || 0)) },
                { title: 'W' + item.w + '"H' + item.h + '"D' + item.d + '"', value: parseFloat(item.size.toFixed(4)) },
                { value: Math.max(0, parseFloat(epoq?.times(item.size || 0).toFixed(1) || '0') || 0) },
            ]));
        }
        result.sort((a, b) => (b[b.length - 5].value as number) - (a[a.length - 5].value as number) || ((b[0].value as number) >= (a[0].value as number) ? 1 : -1));
        return result;
    }, [items, itt, opt, props, formatter]);
    let sum = React.useMemo(() => {
        let result = [0, 0];
        for (let row of body) {
            result[0] += parseFloat(row[11].value.toString().replace(/[^\-\d\.]+/g, ''));
            result[1] += parseFloat(row[13].value.toString().replace(/[^\-\d\.]+/g, ''));
        }
        return result;
    }, [body]);
    React.useImperativeHandle(ref, () => (() => {
        const file = new ExcelJS.Workbook();
        const sheet = file.addWorksheet();
        let r = 1, c = 1;
        let row = sheet.getRow(r);
        for (const { title } of head) {
            row.getCell(c++).value = title;
        }
        for (const data of body) {
            let row = sheet.getRow(++r);
            c = 1;
            for (const { value } of data) {
                row.getCell(c++).value = value;
            }
        }
        file.xlsx.writeBuffer().then(buffer => {
            let a = document.createElement('a');
            a.download = 'export.xlsx';
            a.href = URL.createObjectURL(new Blob([buffer]));
            document.body.append(a);
            a.click();
            URL.revokeObjectURL(a.href);
            a.remove();
        });
    }), [body, head]);
    React.useEffect(() => {
        (async () => {
            let last = sessionStorage.getItem('sync');
            if (!last || parseInt(last) < Date.now()) {
                await syncProducts();
                await syncStockQty();
                await syncSalesOrders();
                await syncPurchaseOrders();
                await syncCreditMemo();
                sessionStorage.setItem('sync', (Date.now() + 3600000).toString());
            }
            let date = fromZonedTime(Date.now(), props.timezone);
            let ps = [], items: Record<string, Row> = {}, year = date.getFullYear(), month = date.getMonth() + 1, lt = getUnixTime(fromZonedTime(startOfMonth(Date.now()), props.timezone));
            ps.push(getProducts({ filter: { group: props.product_group } }));
            ps.push(getStockQty({ filter: { group: props.product_group } }));
            ps.push(getSoldQty({
                filter: {
                    group: props.product_group, 'order.dismiss': 0,
                    'order.completed_at': {
                        gte: (new BigNumber(lt)).minus((new BigNumber(86400)).times(diff(month - 1, month - parseInt(props.stat_sale ?? '12'), year))).integerValue().toNumber(),
                        lt
                    }
                }
            }));
            ps.push(getRefundQty({
                filter: {
                    group: props.product_group, 'credit.dismiss': 0,
                    'credit.status': 'Completed',
                    'credit.created_at': {
                        gte: (new BigNumber(lt)).minus((new BigNumber(86400)).times(diff(month - 1, month - parseInt(props.stat_sale ?? '12'), year))).integerValue().toNumber(),
                        lt
                    }
                }
            }));
            ps.push(getSoldQty({
                filter: {
                    group: props.product_group, 'order.dismiss': 0,
                    'order.completed_at': {
                        gte: (new BigNumber(lt)).minus((new BigNumber(86400)).times(diff(month - 1, month - 3, year))).integerValue().toNumber(),
                        lt
                    }
                }
            }));
            ps.push(getRefundQty({
                filter: {
                    group: props.product_group, 'credit.dismiss': 0,
                    'credit.status': 'Completed',
                    'credit.created_at': {
                        gte: (new BigNumber(lt)).minus((new BigNumber(86400)).times(diff(month - 1, month - 3, year))).integerValue().toNumber(),
                        lt
                    }
                }
            }));
            ps.push(getPurchaseOrderItem({ filter: { group: props.product_group, 'order.dismiss': 0, 'order.status': 'Placed' }, limit: -1 }));
            let { 0: products, 1: stock, 2: sold, 3: refund, 4: mq, 5: rmq, 6: opr } = await Promise.all(ps), days = 1;
            if (typeof products !== 'string') {
                for (let i of (products as ListResponse<Product>).data) {
                    items[i.sku] = {
                        sku: i.sku, price: i.price, w: parseFloat(i.width.toString()), h: parseFloat(i.height.toString()), d: parseFloat(i.depth.toString()),
                        size: (new BigNumber(i.width)).times(i.height).times(i.depth).div(props.unit === 'in' ? '61023.74409473228395' : (props.unit === 'ft' ? '35.31466672148859025' : '1')).toNumber()
                    };
                }
            }
            if (typeof stock !== 'string') {
                for (let i of (stock as ListResponse<SkuQty>).data) {
                    items[i.sku] ??= { sku: i.sku, price: 0, size: 0, w: 0, h: 0, d: 0 };
                    items[i.sku].aq = parseFloat((i.aq ?? i.qty).toString());
                }
            }
            days = diff(month - 1, month - parseInt(props.stat_sale ?? '12'), year);
            if (typeof sold !== 'string') {
                for (let i of (sold as ListResponse<SkuQty>).data) {
                    items[i.sku] ??= { sku: i.sku, price: 0, size: 0, w: 0, h: 0, d: 0 };
                    items[i.sku].ausd = days;
                    items[i.sku].aus = (new BigNumber(i.qty)).div(days);
                    items[i.sku].ausq = parseFloat(i.qty.toString());
                }
            }
            if (typeof refund !== 'string') {
                for (let i of (refund as ListResponse<SkuQty>).data) {
                    if (typeof items[i.sku]?.aus !== 'undefined') {
                        items[i.sku].aus = items[i.sku].aus!.minus((new BigNumber(i.qty)).div(days));
                        items[i.sku].ref = parseFloat(i.qty.toString());
                    }
                }
            }
            days = diff(month - 1, month - 3, year);
            if (typeof mq !== 'string') {
                for (let i of (mq as ListResponse<SkuQty>).data) {
                    items[i.sku] ??= { sku: i.sku, price: 0, size: 0, w: 0, h: 0, d: 0 };
                    items[i.sku].mqd = days;
                    items[i.sku].mq = (new BigNumber(i.qty)).div(days);
                    items[i.sku].mqq = parseFloat(i.qty.toString());
                }
            }
            if (typeof rmq !== 'string') {
                for (let i of (rmq as ListResponse<SkuQty>).data) {
                    if (typeof items[i.sku]?.mq !== 'undefined') {
                        items[i.sku].mq = items[i.sku].mq!.minus((new BigNumber(i.qty)).div(days));
                        items[i.sku].rmq = parseFloat(i.qty.toString());
                    }
                }
            }
            if (typeof opr !== 'string') {
                for (let i of (opr as ListResponse<PurchaseOrderItem>).data) {
                    items[i.sku] ??= { sku: i.sku, price: 0, size: 0, w: 0, h: 0, d: 0 };
                    items[i.sku].op = (items[i.sku].op ?? new BigNumber(0)).plus(i.ordered_qty);
                    items[i.sku].ordered_at = Math.min((items[i.sku].ordered_at || Number.MAX_SAFE_INTEGER), i.ordered_at);
                    items[i.sku].delivered_at = i.delivered_at ? Math.min((items[i.sku].delivered_at || Number.MAX_SAFE_INTEGER), i.delivered_at) : (items[i.sku].delivered_at || null);
                }
            }
            setItems(Object.values(items));
        })();
    }, [props, setItems]);
    return items.length ? (
        <MDBCard className="mt-4">
            <MDBCardBody>
                <MDBTable bordered hover responsive small className="text-center">
                    <MDBTableHead>
                        <tr>
                            {head.map(h => (
                                <th key={h.key} className={h.className}>{h.key}
                                    <MDBTooltip wrapperClass="thead-tip" wrapperProps={{ color: 'link' }} title={h.title}>
                                        <FontAwesomeIcon icon={faInfoCircle} />
                                    </MDBTooltip>
                                </th>
                            ))}
                        </tr>
                    </MDBTableHead>
                    <MDBTableBody>
                        {body.filter(row => filter ? parseInt(row[9].value.toString()) > 0 : true).map(row => (
                            <tr key={row[0].value}>
                                {row.map((cell, i) => (
                                    <td key={row[0].value + '-' + i} title={cell.title} className={cell.className}>{cell.value}</td>
                                ))}
                            </tr>
                        ))}
                    </MDBTableBody>
                    <tfoot>
                        <tr>
                            <td colSpan={10} className="text-start"><MDBBtn color="link" onClick={setFilter.bind(null, !filter)}><FontAwesomeIcon icon={filter ? faAnglesDown : faAnglesUp} /></MDBBtn></td>
                            <th className="text-right">{__('Total')}</th>
                            <td>{formatter.format(sum[0])}</td>
                            <td></td>
                            <td>{parseFloat(sum[1].toFixed(1))}</td>
                        </tr>
                    </tfoot>
                </MDBTable>
            </MDBCardBody>
        </MDBCard>
    ) : (
        <div className="loading bg-white mt-4 text-center p-3"><FontAwesomeIcon icon={faSpinner} spin size="4x" /></div>
    );
});
